import * as React from 'react';
import {ReactNode, useEffect, useRef, useState} from 'react';
import {
    CheckBox,
    PriorityIcon,
    Icon,
    ListItem,
    Flex,
    DropdownHeader,
    DropdownSection
} from '@priority-software/priority-style-react';
import classNames from 'classnames';
import {ItemsList} from '../../../ItemsList';
import {highlightText} from '../../../../../_legacy/library/utils/highlightText';
import {getLocalizedText, texts} from '../../../../../constants/TextConst';
import {getItemValue} from '../../utils/search';
import {isEnglish} from '../../../../../Localization';
import {
    getCurrentFormStatusColor,
    getStatusBorderColor
} from '../../../../../_legacy/Form/utils/status';
import {FieldSearchDropdownProps} from '../../types/DropdownProps';
import {Label} from '../../../Label';
import {useStringDirection} from '../../../../../hooks';
import styles from './SearchOptions.module.scss';
import {createDataTestId} from '../../../../../utils/dataTestId';

function getStrings(item, searchByTitle) {
    // reverse the strings order for english - so the english Description will appear before the description
    if (isEnglish() && !searchByTitle) {
        return {...item, string2: item.string3, string3: item.string2};
    }
    return item;
}

interface RowProps {
    string1: string;
    string2: string;
    string3: string;
    searchQuery: string;
    ignorecase: boolean;
    searchFromBeginning: boolean;
    isSelected: boolean;
    isMulti: boolean;
}

const NewTriggerRow = (props: RowProps) => {
    const {
        string1,
        string2,
        string3,
        searchQuery,
        ignorecase,
        searchFromBeginning,
        isMulti,
        isSelected
    } = props;
    const directionString1 = useStringDirection(string1);
    const directionString2 = useStringDirection(string2);
    const directionString3 = useStringDirection(string3);

    return (
        <>
            <div className={styles.flexStartRow}>
                <Label
                    skin="paragraph-2"
                    textColor="primary-700"
                    componentClasses={styles.wrapline}
                >
                    <span
                        className={classNames({
                            forceLtr: directionString3.forceLtr,
                            forceRtl: directionString3.forceRtl
                        })}
                    >
                        {highlightText(string3, searchQuery, ignorecase, searchFromBeginning, true)}
                    </span>
                </Label>

                {string2 && string3 && (
                    <Label
                        skin="paragraph-2"
                        textColor="primary-700"
                        componentClasses={styles.bullet}
                    >
                        {'\u2022'}
                    </Label>
                )}
                <Label
                    skin="paragraph-2"
                    textColor="primary-700"
                    componentClasses={styles.wrapline}
                >
                    <span
                        className={classNames({
                            forceLtr: directionString2.forceLtr,
                            forceRtl: directionString2.forceRtl
                        })}
                    >
                        {highlightText(string2, searchQuery, ignorecase, searchFromBeginning, true)}
                    </span>
                </Label>
            </div>

            <Label skin="paragraph-2" textColor="secondary-500">
                <span
                    className={classNames({
                        forceLtr: directionString1.forceLtr,
                        forceRtl: directionString1.forceRtl
                    })}
                >
                    {highlightText(string1, searchQuery, ignorecase, searchFromBeginning, true)}
                </span>
            </Label>

            {!isMulti && isSelected && <Icon icon={PriorityIcon.check} iconColor="primary-500" />}
        </>
    );
};

const OldTriggerRow = (props: RowProps) => {
    const {string1, string2, searchQuery, ignorecase, searchFromBeginning, isMulti, isSelected} =
        props;
    const directionString1 = useStringDirection(string1);
    const directionString2 = useStringDirection(string2);
    return (
        <>
            <Label
                skin="paragraph-2"
                textColor="primary-700"
                componentClasses={classNames(styles.wrapline, styles.flexStartRow)}
            >
                <span
                    className={classNames({
                        forceLtr: directionString1.forceLtr,
                        forceRtl: directionString1.forceRtl
                    })}
                >
                    {highlightText(string1, searchQuery, ignorecase, searchFromBeginning, true)}
                </span>
            </Label>
            <Label
                skin="paragraph-2"
                textColor="secondary-500"
                componentClasses={classNames(styles.wrapline, styles.secondOption)}
            >
                <span
                    className={classNames({
                        forceLtr: directionString2.forceLtr,
                        forceRtl: directionString2.forceRtl
                    })}
                >
                    {string2}
                </span>
            </Label>
            {!isMulti && isSelected && <Icon icon={PriorityIcon.check} iconColor="primary-500" />}
        </>
    );
};

const SearchListItem = React.memo(
    React.forwardRef((props: any, ref: any) => {
        const {item, onSelectItem, isMulti, searchByTitle, isSelected, isFocused} = props;

        return (
            <ListItem
                ref={ref}
                isActive={isSelected}
                isFocused={isFocused}
                componentClasses={styles.listItem}
                onClick={event => onSelectItem(item, event)}
            >
                <div className={styles.groupItems}>
                    {isMulti && (
                        <CheckBox componentClasses={styles.optionsCheckbox} checked={isSelected} />
                    )}
                    <div className={styles.itemRow}>
                        {searchByTitle ? (
                            <OldTriggerRow {...props} {...getStrings(item, searchByTitle)} />
                        ) : (
                            <NewTriggerRow {...props} {...getStrings(item, searchByTitle)} />
                        )}
                    </div>
                </div>
            </ListItem>
        );
    })
);

// @observer TODO: check need of observer when refactor filters search control
export const SearchOptions = (props: FieldSearchDropdownProps) => {
    const {searchByTitle, onSearchByChange} = props;
    return (
        <>
            {searchByTitle && (
                <Flex
                    componentClasses="search-header-container"
                    {...createDataTestId('input-search-option-header', true)}
                >
                    <DropdownHeader
                        title={`${getLocalizedText(texts.searchPrefix)} ${searchByTitle}`}
                    />

                    <Label
                        skin="paragraph-2"
                        componentClasses={styles.underline}
                        textColor="primary-500"
                        onClick={onSearchByChange}
                    >
                        {getLocalizedText(texts.changeSearchBy)}
                    </Label>
                </Flex>
            )}
            <OptionsList {...props} gradient renderDropdownItem={SearchListItem} />
        </>
    );
};

const OptionsHeader = React.memo((props: any) => {
    const {title1, title2, sortBy, isDesc, onSortByChange} = props;
    return (
        <Flex componentClasses={styles.optionsHeaderWrapper}>
            {title1 && (
                <DropdownHeader
                    title={`${title1}`}
                    onClick={() => onSortByChange('string1')}
                    icon={
                        sortBy === 'string1' ? (
                            <Icon
                                icon={PriorityIcon.sorted}
                                componentClasses={classNames(styles.sortIcon, {
                                    [styles.desc]: isDesc
                                })}
                            />
                        ) : null
                    }
                />
            )}
            {title2 && (
                <DropdownHeader
                    title={`${title2}`}
                    componentClasses={styles.headerSecondTitle}
                    onClick={() => onSortByChange('string2')}
                    icon={
                        sortBy === 'string2' ? (
                            <Icon
                                icon={PriorityIcon.sorted}
                                componentClasses={classNames(styles.sortIcon, {
                                    [styles.desc]: isDesc
                                })}
                            />
                        ) : null
                    }
                />
            )}
        </Flex>
    );
});

export const StatusCircle = React.memo((props: {status: string}) => {
    // TODO: this.component should be removed after migrating to the new API;
    const {status} = props;
    const color = getCurrentFormStatusColor(status);
    const borderColor = getStatusBorderColor(color);
    return (
        <div
            className={styles.statusCircle}
            style={{backgroundColor: color, borderColor: borderColor}}
        />
    );
});

const ChooseListItem = React.memo(
    React.forwardRef((props: any, ref: any) => {
        const {
            item,
            onSelectItem,
            isMulti,
            searchQuery,
            ignorecase,
            searchFromBeginning,
            isSelected,
            isFocused,
            statusRenderer
        } = props;
        const directionString1 = useStringDirection(item.string1);
        const directionString2 = useStringDirection(item.string2);
        return (
            <ListItem
                ref={ref}
                isActive={isSelected}
                isFocused={isFocused}
                componentClasses={styles.listItem}
                onClick={event => onSelectItem(item, event)}
                {...createDataTestId('input-choose-list-item', true)}
            >
                <div className={styles.itemRow}>
                    {statusRenderer?.(item.string1)}

                    <div className={styles.groupItems}>
                        {isMulti && <CheckBox checked={isSelected} />}
                        <Label
                            textColor="primary-700"
                            skin="paragraph-2"
                            componentClasses={styles.optionsLabel}
                        >
                            <span
                                className={classNames({
                                    forceLtr: directionString1.forceLtr,
                                    forceRtl: directionString1.forceRtl
                                })}
                            >
                                {searchQuery
                                    ? highlightText(
                                          item.string1,
                                          searchQuery,
                                          ignorecase,
                                          searchFromBeginning,
                                          true
                                      )
                                    : item.string1}
                            </span>
                        </Label>

                        {!isMulti && isSelected && (
                            <Icon icon={PriorityIcon.check} iconColor="primary-500" />
                        )}
                    </div>

                    {item.string2 && (
                        <Label
                            textColor="secondary-500"
                            skin="paragraph-2"
                            componentClasses={classNames(styles.optionsLabel, styles.margin)}
                        >
                            <span
                                className={classNames({
                                    forceLtr: directionString2.forceLtr,
                                    forceRtl: directionString2.forceRtl
                                })}
                            >
                                {searchQuery
                                    ? highlightText(
                                          item.string2,
                                          searchQuery,
                                          ignorecase,
                                          searchFromBeginning,
                                          true
                                      )
                                    : item.string2}
                            </span>
                        </Label>
                    )}
                </div>
            </ListItem>
        );
    })
);

//@observer TODO: check need of observer when refactor filters search control
export const ChooseOptions = (props: FieldSearchDropdownProps) => {
    const {title1, title2, isDesc, onSortByChange, sortBy} = props;
    return (
        <>
            <OptionsHeader
                title1={title1}
                title2={title2}
                sortBy={sortBy}
                isDesc={isDesc}
                onSortByChange={onSortByChange}
            />
            <OptionsList {...props} gradient renderDropdownItem={ChooseListItem} />
        </>
    );
};

type OptionsListProps = FieldSearchDropdownProps & {
    gradient: boolean;
    renderDropdownItem: (props) => JSX.Element;
    statusRenderer?: (status: string) => ReactNode;
};

const OptionsList = (props: OptionsListProps) => {
    const {
        results,
        onScroll,
        focusedIndex,
        isLoading,
        renderDropdownItem,
        resultsAutoMaxHeight,
        gradient,
        searchQuery,
        searchByTitle = null,
        isSearching,
        isMulti = false,
        onSelectItem,
        isItemSelected,
        ignorecase,
        searchFromBeginning,
        statusRenderer
    } = props;

    // when displaying message "no results found" we should display the searchQuery that was already processed.
    const [processedQuery, setProcessedQuery] = useState(searchQuery);
    useEffect(() => {
        if (!isSearching) {
            setProcessedQuery(searchQuery);
        }
    }, [isSearching]);

    // when search results change we should scroll the results back to top
    const scrollbar = useRef(null);
    useEffect(() => {
        if (scrollbar.current) {
            scrollbar.current.scrollToTop();
        }
    }, [ignorecase, searchFromBeginning, searchByTitle]);

    const generateKey = (item, idx) => {
        const retval = getItemValue(item);
        return retval + '_' + idx;
    };

    return (
        <DropdownSection gradient={gradient}>
            {results?.length > 0 && (
                <ItemsList
                    autoHeight
                    scrollbarRef={scrollbar}
                    isItemSelected={isItemSelected}
                    focusedIndex={focusedIndex}
                    onScrollEnd={onScroll}
                    items={results}
                    // TODO: check if need when refactor filters search control
                    //extraData={value}
                    autoHeightMax={resultsAutoMaxHeight}
                    renderItem={renderDropdownItem}
                    keyExtractor={generateKey}
                    itemProps={{
                        searchByTitle,
                        isMulti,
                        onSelectItem,
                        searchQuery,
                        ignorecase,
                        searchFromBeginning,
                        statusRenderer
                    }}
                />
            )}
            {!results ||
                (results?.length === 0 && !isLoading && gradient && (
                    <ListItem isActive={false} isFocused={false}>
                        <Label
                            skin="paragraph-2"
                            textColor="secondary-500"
                            componentClasses="no-results-label"
                        >
                            {searchByTitle
                                ? getLocalizedText(texts.noCategorySearchResults)?.replace(
                                      'XXX',
                                      processedQuery
                                  )
                                : getLocalizedText(texts.noSearchResults)}
                        </Label>
                    </ListItem>
                ))}
        </DropdownSection>
    );
};
